// src/features/auth/security/storage/KeyDerivation.ts

import { HashAlgorithm, KeyDerivationOptions } from '../../types/security.types';

export class KeyDerivation {
  private readonly DEFAULT_ITERATIONS = 100000;
  private readonly DEFAULT_KEY_LENGTH = 256;
  private readonly SALT_LENGTH = 16;

  /**
   * Derive key from password
   */
  async deriveKey(
    password: string,
    salt: Uint8Array,
    options?: Partial<KeyDerivationOptions>
  ): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const passwordKey = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: options?.iterations || this.DEFAULT_ITERATIONS,
        hash: options?.algorithm || HashAlgorithm.SHA_256,
      },
      passwordKey,
      {
        name: 'AES-GCM',
        length: options?.keyLength || this.DEFAULT_KEY_LENGTH,
      },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Generate salt
   */
  async generateSalt(): Promise<Uint8Array> {
    return crypto.getRandomValues(new Uint8Array(this.SALT_LENGTH));
  }

  /**
   * Derive key material
   */
  async deriveKeyMaterial(
    password: string,
    salt: Uint8Array,
    length: number
  ): Promise<ArrayBuffer> {
    const encoder = new TextEncoder();
    const passwordKey = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveBits']
    );

    return crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt,
        iterations: this.DEFAULT_ITERATIONS,
        hash: HashAlgorithm.SHA_256,
      },
      passwordKey,
      length * 8 // bits
    );
  }

  /**
   * Hash password
   */
  async hashPassword(password: string, salt: Uint8Array): Promise<string> {
    const keyMaterial = await this.deriveKeyMaterial(password, salt, 32);
    return this.arrayBufferToHex(keyMaterial);
  }

  /**
   * Verify password
   */
  async verifyPassword(
    password: string,
    hash: string,
    salt: Uint8Array
  ): Promise<boolean> {
    const computedHash = await this.hashPassword(password, salt);
    return this.secureCompare(computedHash, hash);
  }

  /**
   * Convert ArrayBuffer to hex string
   */
  private arrayBufferToHex(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    return Array.from(bytes)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Secure string comparison
   */
  private secureCompare(a: string, b: string): boolean {
    if (a.length !== b.length) return false;
    
    let result = 0;
    for (let i = 0; i < a.length; i++) {
      result |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }
    
    return result === 0;
  }
}
